import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "./OIDataAnalysis.css";
import { environment } from "environment/environment";
import useNiftyWebsocket from "service/websocket/NiftyWebsocketApi";
import usePageTitle from "component/common/PageTitle";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const INDEX_PREVIOUS_LTP_DATA_URL = `${environment.baseUrl}/ltp-analysis/index-previous-data`;
const INDEX_OPTION_GREEKS_DATA_URL =
  `${environment.baseUrl}/greeks-analysis/index-option-greeks-data`;

const INDEX_OPTIONS = [
  { label: "NIFTY", value: "NSE:NIFTY" },
  { label: "SENSEX", value: "BSE:SENSEX" },
];

const RANGE_OPTIONS = [
  { label: "ALL", value: "ALL" },
  { label: "ATM", value: "10" },
];

const GREEKS_TIME_OPTIONS = Array.from({ length: 76 }, (_, index) => {
  const totalMinutes = 9 * 60 + 15 + index * 5;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const minutes = String(totalMinutes % 60).padStart(2, "0");

  return `${hours}:${minutes}`;
});

const formatGreeksDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${day}-${month}-${date.getFullYear()}`;
};

const parseGreeksDate = (value) => {
  const match = String(value || "").match(/^(\d{2})-(\d{2})-(\d{4})$/);

  if (!match) {
    return null;
  }

  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
};

const isGreeksWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

const PUT_COLOR = "#59b84d";
const CALL_COLOR = "#c96259";
const PUT_PATTERN_FILL = "rgba(89, 184, 77, 0.18)";
const CALL_PATTERN_FILL = "rgba(201, 98, 89, 0.18)";

const EMPTY_HEADER_STATS = {
  spotPrice: null,
  highestCallVolume: null,
  secondHighestCallVolume: null,
  highestPutVolume: null,
  secondHighestPutVolume: null,
  highestCallOi: null,
  secondHighestCallOi: null,
  highestPutOi: null,
  secondHighestPutOi: null,
  highestCallVolStrike: null,
  secondHighestCallVolStrike: null,
  highestCallOiStrike: null,
  secondHighestCallOiStrike: null,
  highestPutVolStrike: null,
  secondHighestPutVolStrike: null,
  highestPutOiStrike: null,
  secondHighestPutOiStrike: null,
  callTotalVolumeSum: 0,
  callTotalOISum: 0,
  callTotalOICHGSum: 0,
  putTotalVolumeSum: 0,
  putTotalOISum: 0,
  putTotalOICHGSum: 0,
};

const parseMaybeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumber = (value) => parseMaybeNumber(value) ?? 0;
const clampNonNegative = (value) => Math.max(0, toNumber(value));

const pickFirstFiniteNumber = (...values) => {
  for (const value of values) {
    const parsed = parseMaybeNumber(value);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

const isPointDifferenceWithinLimit = (value1, value2, limit = 10) => {
  const firstValue = parseMaybeNumber(value1);
  const secondValue = parseMaybeNumber(value2);

  if (firstValue == null || secondValue == null) {
    return false;
  }

  return Math.abs(firstValue - secondValue) <= limit;
};

const trimTrailingZeroes = (value) =>
  value.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");

const formatNumber = (value, digits = 0) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
};

const formatPrice = (value, digits = 1) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
};

const formatCompactIndianValue = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  const sign = parsed < 0 ? "-" : "";
  const absoluteValue = Math.abs(parsed);

  if (absoluteValue >= 10000000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 10000000).toFixed(absoluteValue >= 100000000 ? 0 : digits)
    )}Cr`;
  }

  if (absoluteValue >= 100000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 100000).toFixed(absoluteValue >= 1000000 ? 0 : digits)
    )}L`;
  }

  if (absoluteValue >= 1000) {
    return `${sign}${new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(absoluteValue)}`;
  }

  return `${sign}${trimTrailingZeroes(absoluteValue.toFixed(digits))}`;
};

const formatAxisValue = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  const sign = parsed < 0 ? "-" : "";
  const absoluteValue = Math.abs(parsed);

  if (absoluteValue >= 10000000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 10000000).toFixed(absoluteValue % 10000000 === 0 ? 0 : 1)
    )}Cr`;
  }

  if (absoluteValue >= 100000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 100000).toFixed(absoluteValue % 100000 === 0 ? 0 : 1)
    )}L`;
  }

  return `${sign}${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(absoluteValue)}`;
};

const formatRatio = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null || !Number.isFinite(parsed)) {
    return "--";
  }

  return parsed.toFixed(2);
};

const formatPlainNumber = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return parsed.toFixed(digits);
};

const formatPercentValue = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return `${trimTrailingZeroes(parsed.toFixed(digits))}%`;
};

const getLtpRangeTierClass = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "";
  }

  if (parsed >= 90) {
    return "oi-ltp-range-tier-blue";
  }

  if (parsed >= 80) {
    return "oi-ltp-range-tier-yellow";
  }

  if (parsed >= 70) {
    return "oi-ltp-range-tier-pink";
  }

  return "";
};

const getMaxHighLowPercentage = (lowValue, highValue) => {
  const low = parseMaybeNumber(lowValue);
  const high = parseMaybeNumber(highValue);

  if (low == null || high == null || high === 0) {
    return null;
  }

  return ((high - low) / high) * 100;
};

const getMaxHighLtpPercentage = (ltpValue, highValue) => {
  const ltp = parseMaybeNumber(ltpValue);
  const high = parseMaybeNumber(highValue);

  if (ltp == null || high == null || high === 0) {
    return null;
  }

  return ((high - ltp) / high) * 100;
};

const formatSpotSymbolName = (value) => {
  const rawValue = typeof value === "string" ? value.trim() : "";

  if (!rawValue) {
    return "SPOT";
  }

  const symbolWithoutExchange = rawValue.includes(":") ? rawValue.split(":").pop() || rawValue : rawValue;
  const normalizedSymbol = symbolWithoutExchange.replace(/-INDEX$/i, "-SPOT");

  if (/^NIFTY$/i.test(normalizedSymbol)) {
    return "NIFTY50-SPOT";
  }

  if (/^SENSEX$/i.test(normalizedSymbol)) {
    return "SENSEX-SPOT";
  }

  return normalizedSymbol;
};

const buildExpirySymbol = (expiryDate) => {
  const match = String(expiryDate || "").match(/^(\d{2})-(\d{2})-(\d{4})$/);

  if (!match) {
    return "";
  }

  const [, day, month, year] = match;
  return `${year.slice(-2)}${Number(month)}${day}`;
};

const normalizePreviousLtpRows = (payload) => {
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return rows
    .map((row) => ({
      ...row,
      strikePrice: row.strikePrice ?? row.indexStrikePrice,
    }))
    .filter((row) => parseMaybeNumber(row.strikePrice) != null);
};

const formatChartDate = (value) => {
  const candidates = [value, value ? String(value).replace(" ", "T") : null];
  let resolvedDate = null;

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const parsedDate = new Date(candidate);

    if (!Number.isNaN(parsedDate.getTime())) {
      resolvedDate = parsedDate;
      break;
    }
  }

  if (!resolvedDate) {
    resolvedDate = new Date();
  }

  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(resolvedDate);
};

const sortByStrike = (rows = []) =>
  [...rows].sort((firstRow, secondRow) => {
    return toNumber(firstRow.strikePrice) - toNumber(secondRow.strikePrice);
  });

const filterOptionRows = (rows, spotPrice, rangeKey) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const sideCount = Number(rangeKey);
  const validSpotPrice = parseMaybeNumber(spotPrice);

  if (!Number.isFinite(sideCount) || sideCount <= 0 || validSpotPrice == null) {
    return rows;
  }

  const closestIndex = rows.reduce((closestRowIndex, row, rowIndex) => {
    const currentDifference = Math.abs(toNumber(row.strikePrice) - validSpotPrice);
    const closestDifference = Math.abs(
      toNumber(rows[closestRowIndex].strikePrice) - validSpotPrice
    );

    return currentDifference < closestDifference ? rowIndex : closestRowIndex;
  }, 0);

  const startIndex = Math.max(0, closestIndex - sideCount);
  const endIndex = Math.min(rows.length - 1, closestIndex + sideCount);

  return rows.slice(startIndex, endIndex + 1);
};

const parseStructuredList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return [];
    }

    try {
      return parseStructuredList(JSON.parse(trimmedValue));
    } catch {
      return [];
    }
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value.data)) {
      return value.data;
    }

    if (Array.isArray(value.list)) {
      return value.list;
    }

    if (Array.isArray(value.rows)) {
      return value.rows;
    }
  }

  return [];
};

const normalizeGreeksIndexData = (payload) => {
  if (typeof payload === "string") {
    try {
      return normalizeGreeksIndexData(JSON.parse(payload));
    } catch {
      return [];
    }
  }

  if (Array.isArray(payload)) {
    return payload.filter(
      (item) => item && typeof item === "object" && Array.isArray(item.indexOptionDataModelList)
    );
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.indexOptionDataModelList)) {
    return [payload];
  }

  const nestedPayload = payload.data ?? payload.list ?? payload.rows;
  return nestedPayload == null ? [] : normalizeGreeksIndexData(nestedPayload);
};

const hasNestedListSource = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "string") {
    return value.trim().length > 0 && value.trim() !== "[]";
  }

  if (!value || typeof value !== "object") {
    return false;
  }

  return (
    (Array.isArray(value.data) && value.data.length > 0) ||
    (Array.isArray(value.list) && value.list.length > 0) ||
    (Array.isArray(value.rows) && value.rows.length > 0)
  );
};

const resolveNestedListSource = (marketData, rows, key) => {
  if (Array.isArray(rows)) {
    const rowSource = rows.find((row) => hasNestedListSource(row?.[key]));

    if (rowSource?.[key] != null) {
      return rowSource[key];
    }

    const fallbackRowSource = rows.find((row) => row?.[key] != null);

    if (fallbackRowSource?.[key] != null) {
      return fallbackRowSource[key];
    }
  }

  if (marketData?.[key] != null) {
    return marketData[key];
  }

  return null;
};

const resolveFirstNestedListSource = (marketData, rows, keys = []) => {
  for (const key of keys) {
    const resolvedSource = resolveNestedListSource(marketData, rows, key);

    if (hasNestedListSource(resolvedSource)) {
      return resolvedSource;
    }
  }

  return null;
};

const formatMinuteRangeLabel = (startValue, endValue) => {
  const trimmedStart = startValue.trim();
  const trimmedEnd = endValue.trim();
  const startTimeMatch = trimmedStart.match(/^(\d{1,2}):(\d{2})$/);
  const endTimeMatch = trimmedEnd.match(/^(\d{1,2}):(\d{2})$/);

  if (startTimeMatch && endTimeMatch && startTimeMatch[1] === endTimeMatch[1]) {
    return `${trimmedStart} - ${endTimeMatch[2]}`;
  }

  return `${trimmedStart} - ${trimmedEnd}`;
};

const parseMinuteTimeValue = (value) => {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const matchedTime = value.trim().match(/(\d{1,2}):(\d{2})/);

  if (!matchedTime) {
    return null;
  }

  const hours = parseMaybeNumber(matchedTime[1]);
  const minutes = parseMaybeNumber(matchedTime[2]);

  if (hours == null || minutes == null) {
    return null;
  }

  return (hours * 60) + minutes;
};

const extractMinuteSortValue = (entry, minuteLabel) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return parseMinuteTimeValue(minuteLabel);
  }

  const directTimeValue = [
    entry.intervalStartTime,
    entry.intervalStart,
    entry.startTime,
    entry.fromTime,
    entry.start,
    entry.startMinute,
    entry.minute,
    entry.minuteLabel,
    entry.minuteRange,
    entry.timeRange,
    entry.label,
    minuteLabel,
  ].find((value) => parseMinuteTimeValue(value) != null);

  return parseMinuteTimeValue(directTimeValue);
};

const extractMinuteVolumeLabel = (entry, fallbackIndex) => {
  if (typeof entry === "string") {
    return entry.trim() || `Minute ${fallbackIndex + 1}`;
  }

  if (Array.isArray(entry)) {
    const textValue = entry.find((value) => typeof value === "string" && value.trim());
    return textValue?.trim() || `Minute ${fallbackIndex + 1}`;
  }

  if (!entry || typeof entry !== "object") {
    return `Minute ${fallbackIndex + 1}`;
  }

  const directLabel = [
    entry.minute,
    entry.minuteLabel,
    entry.minuteRange,
    entry.timeRange,
    entry.label,
    entry.interval,
    entry.slot,
    entry.timeSlot,
    entry.bucketLabel,
    entry.bucket,
    entry.range,
    entry.intervalLabel,
  ].find((value) => typeof value === "string" && value.trim());

  if (directLabel) {
    return directLabel.trim();
  }

  const startValue = [
    entry.intervalStartTime,
    entry.startTime,
    entry.fromTime,
    entry.start,
    entry.startMinute,
    entry.intervalStart,
  ].find((value) => typeof value === "string" && value.trim());
  const endValue = [
    entry.intervalEndTime,
    entry.endTime,
    entry.toTime,
    entry.end,
    entry.endMinute,
    entry.intervalEnd,
  ].find((value) => typeof value === "string" && value.trim());

  if (startValue && endValue) {
    return formatMinuteRangeLabel(startValue, endValue);
  }

  return `Minute ${fallbackIndex + 1}`;
};

const extractMinuteVolumeValue = (entry, side) => {
  if (typeof entry === "number") {
    return entry;
  }

  if (Array.isArray(entry)) {
    const lastNumericValue = [...entry]
      .reverse()
      .find((value) => parseMaybeNumber(value) != null);

    return parseMaybeNumber(lastNumericValue);
  }

  if (!entry || typeof entry !== "object") {
    return parseMaybeNumber(entry);
  }

  return pickFirstFiniteNumber(
    side === "call" ? entry.ceCurrentVolume : entry.peCurrentVolume,
    side === "call" ? entry.ceStartVolume : entry.peStartVolume,
    entry.currentVolume,
    entry.startVolume,
    side === "call" ? entry.totalCallVolume : entry.totalPutVolume,
    side === "call" ? entry.callVolume : entry.putVolume,
    side === "call" ? entry.ceVolume : entry.peVolume,
    entry.totalVolume,
    entry.volume,
    entry.value,
    entry.total,
    entry.amount,
    entry.y
  );
};

const extractMinuteIntervalVolume = (entry, side) => {
  if (typeof entry === "number") {
    return entry;
  }

  if (Array.isArray(entry)) {
    return extractMinuteVolumeValue(entry, side);
  }

  if (!entry || typeof entry !== "object") {
    return parseMaybeNumber(entry);
  }

  const sideCurrentVolume = pickFirstFiniteNumber(
    side === "call" ? entry.ceCurrentVolume : entry.peCurrentVolume,
    entry.currentVolume
  );
  const sideStartVolume = pickFirstFiniteNumber(
    side === "call" ? entry.ceStartVolume : entry.peStartVolume,
    entry.startVolume
  );

  return pickFirstFiniteNumber(
    side === "call" ? entry.ceVolumeSum : entry.peVolumeSum,
    side === "call" ? entry.callVolumeSum : entry.putVolumeSum,
    entry.minuteVolume,
    entry.oneMinuteVolume,
    entry.volumeChange,
    sideCurrentVolume != null && sideStartVolume != null
      ? toNumber(sideCurrentVolume) - toNumber(sideStartVolume)
      : null,
    entry.currentVolume != null && entry.startVolume != null
      ? toNumber(entry.currentVolume) - toNumber(entry.startVolume)
      : null,
    side === "call" ? entry.totalCallVolume : entry.totalPutVolume,
    side === "call" ? entry.callVolume : entry.putVolume,
    side === "call" ? entry.ceVolume : entry.peVolume,
    entry.totalVolume,
    entry.volume,
    entry.value,
    entry.total,
    entry.amount,
    entry.y
  );
};

const extractMinuteVolumeRank = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  return parseMaybeNumber(entry.rank ?? entry.position ?? entry.order);
};

const buildMinuteVolumePanelData = (rawList, side) => {
  const parsedEntries = parseStructuredList(rawList)
    .map((entry, index) => {
      const minuteLabel = extractMinuteVolumeLabel(entry, index);

      return {
        minuteLabel,
        rank: extractMinuteVolumeRank(entry),
        sourceIndex: index,
        sortValue: extractMinuteSortValue(entry, minuteLabel),
        volume: extractMinuteVolumeValue(entry, side),
        intervalVolume: extractMinuteIntervalVolume(entry, side),
      };
    })
    .filter((entry) => entry.volume != null || entry.intervalVolume != null);

  if (parsedEntries.length === 0) {
    return {
      rows: [],
      maxVolume: 0,
      totalVolume: 0,
    };
  }

  const hasSortValue = parsedEntries.some((entry) => entry.sortValue != null);
  const hasExplicitRank = parsedEntries.some((entry) => entry.rank != null);
  const orderedEntries = [...parsedEntries].sort((firstEntry, secondEntry) => {
    if (hasSortValue) {
      const sortDifference = toNumber(secondEntry.sortValue) - toNumber(firstEntry.sortValue);

      if (sortDifference !== 0) {
        return sortDifference;
      }
    }

    if (hasExplicitRank) {
      const rankDifference = toNumber(firstEntry.rank) - toNumber(secondEntry.rank);

      if (rankDifference !== 0) {
        return rankDifference;
      }
    }

    return toNumber(firstEntry.sourceIndex) - toNumber(secondEntry.sourceIndex);
  });

  let runningTotalVolume = 0;
  const rows = orderedEntries.slice(0, 10).map((entry, index) => {
    const barVolume = entry.intervalVolume ?? entry.volume;
    const totalContribution = entry.intervalVolume ?? entry.volume;
    runningTotalVolume += toNumber(totalContribution);

    return {
      ...entry,
      rank: index + 1,
      barVolume,
      totalVolume: runningTotalVolume,
    };
  });

  return {
    rows,
    maxVolume: rows.reduce((maximumValue, entry) => {
      return Math.max(maximumValue, toNumber(entry.barVolume));
    }, 0),
    totalVolume: runningTotalVolume,
  };
};

const buildMetrics = (rows = []) => {
  const callOiTotal = rows.reduce((sum, row) => sum + toNumber(row.ceOI), 0);
  const putOiTotal = rows.reduce((sum, row) => sum + toNumber(row.peOI), 0);
  const callOiChangeTotal = rows.reduce((sum, row) => sum + toNumber(row.ceOIChg), 0);
  const putOiChangeTotal = rows.reduce((sum, row) => sum + toNumber(row.peOIChg), 0);
  const callVolumeTotal = rows.reduce((sum, row) => sum + toNumber(row.ceVolume), 0);
  const putVolumeTotal = rows.reduce((sum, row) => sum + toNumber(row.peVolume), 0);

  return {
    callOiTotal,
    putOiTotal,
    callOiChangeTotal,
    putOiChangeTotal,
    callVolumeTotal,
    putVolumeTotal,
    pcrOi: callOiTotal !== 0 ? putOiTotal / callOiTotal : null,
    pcrOiChange: callOiChangeTotal !== 0 ? putOiChangeTotal / callOiChangeTotal : null,
    pcrVolume: callVolumeTotal !== 0 ? putVolumeTotal / callVolumeTotal : null,
  };
};

const buildHeaderStats = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return EMPTY_HEADER_STATS;
  }

  const sortDesc = (values = []) => [...new Set(values)].sort((firstValue, secondValue) => {
    return secondValue - firstValue;
  });

  const extract = (key) =>
    rows.map((row) => parseMaybeNumber(row[key])).filter((value) => value != null);

  const findStrike = (key, targetValue) => {
    if (targetValue == null) {
      return null;
    }

    return rows.find((row) => parseMaybeNumber(row[key]) === targetValue)?.strikePrice ?? null;
  };

  const sortedCallVolume = sortDesc(extract("ceVolume"));
  const sortedPutVolume = sortDesc(extract("peVolume"));
  const sortedCallOi = sortDesc(extract("ceOI"));
  const sortedPutOi = sortDesc(extract("peOI"));
  const sortedCallOiChange = sortDesc(extract("ceOIChg"));
  const sortedPutOiChange = sortDesc(extract("peOIChg"));

  return {
    spotPrice: pickFirstFiniteNumber(rows[0]?.spotPrice, rows[0]?.niftySpotPrice),
    highestCallVolume: sortedCallVolume[0] ?? null,
    secondHighestCallVolume: sortedCallVolume[1] ?? null,
    highestPutVolume: sortedPutVolume[0] ?? null,
    secondHighestPutVolume: sortedPutVolume[1] ?? null,
    highestCallOi: sortedCallOi[0] ?? null,
    secondHighestCallOi: sortedCallOi[1] ?? null,
    highestPutOi: sortedPutOi[0] ?? null,
    secondHighestPutOi: sortedPutOi[1] ?? null,
    highestCallVolStrike: findStrike("ceVolume", sortedCallVolume[0]),
    secondHighestCallVolStrike: findStrike("ceVolume", sortedCallVolume[1]),
    highestCallOiStrike: findStrike("ceOI", sortedCallOi[0]),
    secondHighestCallOiStrike: findStrike("ceOI", sortedCallOi[1]),
    highestPutVolStrike: findStrike("peVolume", sortedPutVolume[0]),
    secondHighestPutVolStrike: findStrike("peVolume", sortedPutVolume[1]),
    highestPutOiStrike: findStrike("peOI", sortedPutOi[0]),
    secondHighestPutOiStrike: findStrike("peOI", sortedPutOi[1]),
    callTotalVolumeSum: sortedCallVolume.reduce((sum, value) => sum + value, 0),
    callTotalOISum: sortedCallOi.reduce((sum, value) => sum + value, 0),
    callTotalOICHGSum: sortedCallOiChange.reduce((sum, value) => sum + value, 0),
    putTotalVolumeSum: sortedPutVolume.reduce((sum, value) => sum + value, 0),
    putTotalOISum: sortedPutOi.reduce((sum, value) => sum + value, 0),
    putTotalOICHGSum: sortedPutOiChange.reduce((sum, value) => sum + value, 0),
  };
};

const getPercentage = (value, highest) => (highest ? ((value / highest) * 100).toFixed(2) : "0");

const getBarPercentageValue = (value, highest) => {
  const safeValue = clampNonNegative(value);
  const safeHighest = clampNonNegative(highest);

  if (!safeHighest || !safeValue) {
    return 0;
  }

  return Math.round((safeValue / safeHighest) * 100);
};

const formatBarPercentageLabel = (value, highest) => {
  return `${getBarPercentageValue(value, highest)}%`;
};

const getVolumeBadgeVariant = (value, highest, secondHighest) => {
  const safeValue = clampNonNegative(value);
  const safeHighest = clampNonNegative(highest);
  const safeSecondHighest = clampNonNegative(secondHighest);

  if (!safeValue || !safeHighest) {
    return "default";
  }

  const percentage = getBarPercentageValue(safeValue, safeHighest);

  if (safeValue === safeHighest) {
    return "strong";
  }

  if (safeValue === safeSecondHighest && percentage >= 75) {
    return "warning";
  }

  if (percentage >= 75 && safeValue < (safeSecondHighest || Number.POSITIVE_INFINITY)) {
    return "bold";
  }

  return "default";
};

const getChartThemeRoot = (chart) => {
  if (typeof document === "undefined") {
    return null;
  }

  return chart?.canvas?.closest(".oi-visual-page") || document.documentElement;
};

const getChartCssVar = (chart, variableName, fallbackValue) => {
  const themeRoot = getChartThemeRoot(chart);

  if (!themeRoot || typeof window === "undefined") {
    return fallbackValue;
  }

  const resolvedValue = window.getComputedStyle(themeRoot).getPropertyValue(variableName).trim();
  return resolvedValue || fallbackValue;
};

const getChartCssNumberVar = (chart, variableName, fallbackValue) => {
  const resolvedValue = Number.parseFloat(getChartCssVar(chart, variableName, String(fallbackValue)));
  return Number.isFinite(resolvedValue) ? resolvedValue : fallbackValue;
};

const getVolumeLabelTheme = (chart) => {
  const fontWeight = getChartCssVar(chart, "--oi-volume-badge-font-weight", "800");
  const boldFontWeight = getChartCssVar(chart, "--oi-volume-badge-bold-font-weight", "900");
  const fontSize = getChartCssVar(chart, "--oi-volume-badge-font-size", "11px");
  const fontFamily = getChartCssVar(chart, "--oi-volume-badge-font-family", '"Segoe UI", sans-serif');

  return {
    textColor: getChartCssVar(chart, "--oi-volume-badge-text", "#0f172a"),
    defaultBackground: getChartCssVar(chart, "--oi-volume-badge-default-bg", "#9bd34f"),
    defaultBorder: getChartCssVar(chart, "--oi-volume-badge-default-border", "#9bd34f"),
    strongBackground: getChartCssVar(chart, "--oi-volume-badge-strong-bg", "rgb(56, 190, 235)"),
    strongBorder: getChartCssVar(chart, "--oi-volume-badge-strong-border", "rgb(56, 190, 235)"),
    warningBackground: getChartCssVar(chart, "--oi-volume-badge-warning-bg", "yellow"),
    warningBorder: getChartCssVar(chart, "--oi-volume-badge-warning-border", "#facc15"),
    font: `${fontWeight} ${fontSize} ${fontFamily}`,
    boldFont: `${boldFontWeight} ${fontSize} ${fontFamily}`,
    horizontalPadding: getChartCssNumberVar(chart, "--oi-volume-badge-padding-x", 10),
    height: getChartCssNumberVar(chart, "--oi-volume-badge-height", 26),
    radius: getChartCssNumberVar(chart, "--oi-volume-badge-radius", 9),
    offset: getChartCssNumberVar(chart, "--oi-volume-badge-offset", 8),
  };
};

const getVolumeLabelBadgeStyle = (theme, variant, strokeColor) => {
  if (variant === "strong") {
    return {
      background: theme.strongBackground,
      border: theme.strongBorder,
      text: theme.textColor,
      font: theme.font,
    };
  }

  if (variant === "warning") {
    return {
      background: theme.warningBackground,
      border: theme.warningBorder,
      text: theme.textColor,
      font: theme.font,
    };
  }

  if (variant === "bold") {
    return {
      background: theme.defaultBackground,
      border: theme.defaultBorder || strokeColor,
      text: theme.textColor,
      font: theme.boldFont,
    };
  }

  return {
    background: theme.defaultBackground,
    border: theme.defaultBorder || strokeColor,
    text: theme.textColor,
    font: theme.font,
  };
};

const getCallResistanceLogic = (highestCallVolStrike, highestCallOiStrike, spotPrice) => {
  if (highestCallVolStrike == null || highestCallOiStrike == null || spotPrice == null) {
    return null;
  }

  if (highestCallVolStrike === highestCallOiStrike) {
    return highestCallVolStrike;
  }

  const volumeDistance = Math.abs(highestCallVolStrike - spotPrice);
  const oiDistance = Math.abs(highestCallOiStrike - spotPrice);

  return volumeDistance < oiDistance ? highestCallVolStrike : highestCallOiStrike;
};

const getCallResistanceWttAndWtb = (
  callResistance,
  secondHighestCallVolStrike,
  secondHighestCallVolume,
  highestCallVolume
) => {
  if (
    callResistance == null ||
    secondHighestCallVolStrike == null ||
    secondHighestCallVolume == null ||
    highestCallVolume == null
  ) {
    return "N/A";
  }

  const percentage = `${getPercentage(secondHighestCallVolume, highestCallVolume)}%`;
  const percentageValue = parseFloat(percentage);

  if (secondHighestCallVolStrike < callResistance) {
    return percentageValue >= 75 ? `WTB - ${percentage}` : "STRNG";
  }

  return percentageValue >= 75 ? `WTT - ${percentage}` : "STRNG";
};

const getPutSupportLogic = (highestPutVolStrike, highestPutOiStrike, spotPrice) => {
  if (highestPutVolStrike == null || highestPutOiStrike == null || spotPrice == null) {
    return null;
  }

  if (highestPutVolStrike === highestPutOiStrike) {
    return highestPutVolStrike;
  }

  const volumeDistance = Math.abs(highestPutVolStrike - spotPrice);
  const oiDistance = Math.abs(highestPutOiStrike - spotPrice);

  return volumeDistance < oiDistance ? highestPutVolStrike : highestPutOiStrike;
};

const getPutSupportWttAndWtb = (
  secondHighestPutVolStrike,
  secondHighestPutVolume,
  putSupport,
  highestPutVolume
) => {
  if (
    putSupport == null ||
    secondHighestPutVolStrike == null ||
    secondHighestPutVolume == null ||
    highestPutVolume == null
  ) {
    return "N/A";
  }

  const percentage = `${getPercentage(secondHighestPutVolume, highestPutVolume)}%`;
  const percentageValue = parseFloat(percentage);

  if (secondHighestPutVolStrike < putSupport) {
    return percentageValue >= 75 ? `WTB - ${percentage}` : "STRNG";
  }

  return percentageValue >= 75 ? `WTT - ${percentage}` : "STRNG";
};

const getBuySellSignal = (stats) => {
  if (!stats) {
    return "NO_SIGNAL";
  }

  const {
    callTotalOICHGSum,
    putTotalOICHGSum,
    callTotalOISum,
    putTotalOISum,
    callTotalVolumeSum,
    putTotalVolumeSum,
  } = stats;

  if (
    callTotalOICHGSum > putTotalOICHGSum &&
    callTotalOISum > putTotalOISum &&
    callTotalVolumeSum < putTotalVolumeSum
  ) {
    return "SELL_ON_RISE";
  }

  if (
    putTotalOICHGSum > callTotalOICHGSum &&
    putTotalOISum > callTotalOISum &&
    putTotalVolumeSum < callTotalVolumeSum
  ) {
    return "BUY_ON_DEEP";
  }

  return "NO_SIGNAL";
};

const getVolumeBuySellSignal = (callVolumeSum, putVolumeSum) => {
  const callVolumeTotal = toNumber(callVolumeSum);
  const putVolumeTotal = toNumber(putVolumeSum);

  if (callVolumeTotal > putVolumeTotal) {
    return "BUY";
  }

  if (putVolumeTotal > callVolumeTotal) {
    return "SELL";
  }

  return "NO SIGNAL";
};

const getVolumeSignalClassName = (signal) =>
  signal === "BUY" ? "is-buy" : signal === "SELL" ? "is-sell" : "is-neutral";

const getMinuteLabelKey = (minuteLabel, fallbackIndex) => {
  const normalizedLabel = typeof minuteLabel === "string" ? minuteLabel.trim().toLowerCase() : "";
  return normalizedLabel || `minute-${fallbackIndex}`;
};

const buildMinuteVolumeSignalRows = (callRows = [], putRows = []) => {
  const rowsByMinute = new Map();

  const addRows = (rows, side) => {
    rows.forEach((row, index) => {
      const minuteKey = getMinuteLabelKey(row.minuteLabel, index);
      const existingRow = rowsByMinute.get(minuteKey) ?? {
        minuteLabel: row.minuteLabel,
        rank: row.rank,
        sortValue: row.sortValue,
        sourceIndex: row.sourceIndex,
        callVolume: null,
        putVolume: null,
      };

      rowsByMinute.set(minuteKey, {
        ...existingRow,
        minuteLabel: existingRow.minuteLabel || row.minuteLabel,
        rank: existingRow.rank ?? row.rank,
        sortValue: existingRow.sortValue ?? row.sortValue,
        sourceIndex: existingRow.sourceIndex ?? row.sourceIndex,
        [side === "call" ? "callVolume" : "putVolume"]: row.barVolume,
      });
    });
  };

  addRows(callRows, "call");
  addRows(putRows, "put");

  const rows = Array.from(rowsByMinute.values()).sort((firstRow, secondRow) => {
    if (firstRow.sortValue != null || secondRow.sortValue != null) {
      const sortDifference = toNumber(secondRow.sortValue) - toNumber(firstRow.sortValue);

      if (sortDifference !== 0) {
        return sortDifference;
      }
    }

    if (firstRow.rank != null || secondRow.rank != null) {
      const rankDifference = toNumber(firstRow.rank) - toNumber(secondRow.rank);

      if (rankDifference !== 0) {
        return rankDifference;
      }
    }

    return toNumber(firstRow.sourceIndex) - toNumber(secondRow.sourceIndex);
  });

  return rows.map((row, index) => {
    const signal = getVolumeBuySellSignal(row.callVolume, row.putVolume);

    return {
      ...row,
      rank: row.rank ?? index + 1,
      signal,
      signalClassName: getVolumeSignalClassName(signal),
    };
  });
};

const getStrikeStep = (rows = []) => {
  const strikeDifferences = rows
    .slice(1)
    .map((row, index) => Math.abs(toNumber(row.strikePrice) - toNumber(rows[index].strikePrice)))
    .filter((difference) => difference > 0);

  if (!strikeDifferences.length) {
    return 50;
  }

  return Math.min(...strikeDifferences);
};

const buildOiSegments = (oiValue, oiChangeValue) => {
  const currentOi = clampNonNegative(oiValue);
  const oiChange = toNumber(oiChangeValue);

  if (oiChange >= 0) {
    const increase = Math.min(oiChange, currentOi);

    return {
      base: Math.max(currentOi - increase, 0),
      increase,
      decrease: 0,
      currentOi,
      changeMagnitude: increase,
      hasDecrease: false,
    };
  }

  return {
    base: currentOi,
    increase: 0,
    decrease: Math.abs(oiChange),
    currentOi,
    changeMagnitude: Math.abs(oiChange),
    hasDecrease: true,
  };
};

const getStripePattern = (chart, strokeColor, fillColor) => {
  if (!chart?.ctx || typeof document === "undefined") {
    return fillColor;
  }

  const cacheKey = `${strokeColor}-${fillColor}`;

  if (!chart.$oiPatternCache) {
    chart.$oiPatternCache = {};
  }

  if (!chart.$oiPatternCache[cacheKey]) {
    const patternCanvas = document.createElement("canvas");
    patternCanvas.width = 12;
    patternCanvas.height = 12;

    const patternContext = patternCanvas.getContext("2d");

    if (!patternContext) {
      return fillColor;
    }

    patternContext.clearRect(0, 0, 12, 12);
    patternContext.fillStyle = fillColor;
    patternContext.fillRect(0, 0, 12, 12);
    patternContext.strokeStyle = strokeColor;
    patternContext.lineWidth = 2;

    patternContext.beginPath();
    patternContext.moveTo(-2, 10);
    patternContext.lineTo(5, 3);
    patternContext.moveTo(2, 14);
    patternContext.lineTo(9, 7);
    patternContext.moveTo(6, 6);
    patternContext.lineTo(13, -1);
    patternContext.stroke();

    chart.$oiPatternCache[cacheKey] = chart.ctx.createPattern(patternCanvas, "repeat") || fillColor;
  }

  return chart.$oiPatternCache[cacheKey];
};

const roundRect = (ctx, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
};

const spotMarkerPlugin = {
  id: "spotMarker",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;

    if (!pluginOptions?.enabled || !chartArea || !xScale) {
      return;
    }

    const spotValue = parseMaybeNumber(pluginOptions.spotValue);

    if (spotValue == null) {
      return;
    }

    const xCoordinate = xScale.getPixelForValue(spotValue);

    if (
      !Number.isFinite(xCoordinate) ||
      xCoordinate < chartArea.left ||
      xCoordinate > chartArea.right
    ) {
      return;
    }

    const label = pluginOptions.label || "";
    const labelHeight = 30;
    const labelY = chartArea.top + 4;

    ctx.save();
    ctx.strokeStyle = pluginOptions.lineColor || "rgba(15, 23, 42, 0.65)";
    ctx.lineWidth = 1.25;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(xCoordinate, label ? labelY + labelHeight + 4 : chartArea.top);
    ctx.lineTo(xCoordinate, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);

    if (label) {
      const horizontalPadding = 12;
      const labelRadius = 12;

      ctx.font = "600 13px 'Segoe UI', sans-serif";

      const textWidth = ctx.measureText(label).width;
      const boxWidth = textWidth + horizontalPadding * 2;
      const boxX = Math.min(
        Math.max(xCoordinate - boxWidth / 2, chartArea.left + 4),
        chartArea.right - boxWidth - 4
      );

      ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
      ctx.strokeStyle = "rgba(15, 23, 42, 0.08)";
      ctx.lineWidth = 1;

      roundRect(ctx, boxX, labelY, boxWidth, labelHeight, labelRadius);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#172033";
      ctx.textBaseline = "middle";
      ctx.fillText(label, boxX + horizontalPadding, labelY + labelHeight / 2);
    }

    ctx.restore();
  },
};

const volumePercentageLabelPlugin = {
  id: "volumePercentageLabel",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    if (!pluginOptions?.enabled) {
      return;
    }

    const { ctx, chartArea } = chart;

    if (!ctx || !chartArea) {
      return;
    }

    const volumeLabelTheme = getVolumeLabelTheme(chart);

    ctx.save();

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!dataset?.showBarTopLabels) {
        return;
      }

      const meta = chart.getDatasetMeta(datasetIndex);
      const valueLabels = Array.isArray(dataset.valueLabels) ? dataset.valueLabels : [];
      const valuePercentages = Array.isArray(dataset.valuePercentages)
        ? dataset.valuePercentages
        : [];
      const valueVariants = Array.isArray(dataset.valueVariants)
        ? dataset.valueVariants
        : [];
      const strokeColor =
        typeof dataset.borderColor === "string" ? dataset.borderColor : "#172033";

      meta.data.forEach((barElement, valueIndex) => {
        const label = valueLabels[valueIndex];
        const percentage = Number(valuePercentages[valueIndex] ?? 0);
        const variant = valueVariants[valueIndex] || "default";
        const yValue = Number(dataset.data?.[valueIndex]?.y ?? 0);

        if (!label || !Number.isFinite(yValue) || yValue <= 0) {
          return;
        }

        const { x, y, base } = barElement;

        if (![x, y, base].every(Number.isFinite)) {
          return;
        }

        const badgeStyle = getVolumeLabelBadgeStyle(volumeLabelTheme, variant, strokeColor);
        const verticalOffset = pluginOptions.labelOffset ?? volumeLabelTheme.offset;

        ctx.font = badgeStyle.font || volumeLabelTheme.font;

        const textWidth = ctx.measureText(label).width;
        const horizontalPadding = volumeLabelTheme.horizontalPadding;
        const boxWidth = textWidth + horizontalPadding * 2;
        const boxHeight = volumeLabelTheme.height;
        const boxX = Math.min(
          Math.max(x - boxWidth / 2, chartArea.left + 4),
          chartArea.right - boxWidth - 4
        );
        const boxY = Math.max(y - boxHeight - verticalOffset, 4);

        ctx.fillStyle = badgeStyle.background;
        ctx.strokeStyle = badgeStyle.border;
        ctx.lineWidth = 1;
        roundRect(ctx, boxX, boxY, boxWidth, boxHeight, volumeLabelTheme.radius);
        ctx.fill();
        if (badgeStyle.border !== badgeStyle.background) {
          ctx.stroke();
        }

        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = badgeStyle.text;
        ctx.fillText(label, boxX + boxWidth / 2, boxY + boxHeight / 2);
      });
    });

    ctx.restore();
  },
};

export default function OIDataAnalysis() {
  const { options, indexGreeksAnalyzeData } = useNiftyWebsocket(
    environment.niftyWebsocketUrl
  );
  const [indexPivotPointModel, setIndexPivotPointModel] = useState(null);
  const [indexExpiryDateList, setIndexExpiryDateList] = useState([]);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState("");
  const [previousExpiryRows, setPreviousExpiryRows] = useState([]);
  const [isPreviousExpiryLoading, setIsPreviousExpiryLoading] = useState(false);
  const [previousExpiryError, setPreviousExpiryError] = useState("");
  const [indexLiveDataModel, setIndexLiveDataModel] = useState(null);
  const [indexSymbolName, setIndexSymbolName] = useState("NIFTY");
  const [allOptionRows, setAllOptionRows] = useState([]);
  const [visibleOptionRows, setVisibleOptionRows] = useState([]);
  const [selectedStrikeFilter, setSelectedStrikeFilter] = useState("10");
  const [indexFilterValueChanges, setIndexFilterValueChangeFilter] = useState("NSE:NIFTY");
  const [selectedGreeksSymbol, setSelectedGreeksSymbol] = useState("");
  const [selectedGreeksDate, setSelectedGreeksDate] = useState("");
  const [greeksStrikeFilter, setGreeksStrikeFilter] = useState("10");
  const [selectedGreeksTime, setSelectedGreeksTime] = useState("09:15");
  const [greeksSearchResult, setGreeksSearchResult] = useState(null);
  const [isGreeksSearchLoading, setIsGreeksSearchLoading] = useState(false);
  const [greeksSearchError, setGreeksSearchError] = useState("");
  const greeksSearchRequestRef = useRef(0);
  const [activeView, setActiveView] = useState("optionOi");
  const [showOiChange, setShowOiChange] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [blinkSignal, setBlinkSignal] = useState(true);
  const marketData = options?.[indexFilterValueChanges] ?? null;
  const currentExpiryDate = marketData?.expiryDate || "";

  useEffect(() => {
    const indexDataList = normalizeGreeksIndexData(indexGreeksAnalyzeData);
    const availableSymbols = indexDataList
      .map((item) => item.indexSymbol)
      .filter(Boolean);

    setSelectedGreeksSymbol((currentSymbol) =>
      availableSymbols.includes(currentSymbol) ? currentSymbol : availableSymbols[0] || ""
    );

    indexDataList.forEach((item) => {
      console.log(
        `${item.indexSymbol} indexOptionDataModelList =>`,
        item.indexOptionDataModelList
      );
    });
  }, [indexGreeksAnalyzeData]);

  useEffect(() => {
    if (!marketData) {
      setIndexLiveDataModel(null);
      setIndexExpiryDateList([]);
      setSelectedExpiryDate("");
      setAllOptionRows([]);
      setVisibleOptionRows([]);
      return;
    }

    setIndexLiveDataModel(marketData.indexLiveDataModel || null);
    setIndexPivotPointModel(marketData.indexPivotPointModel || null);
    const expiryDates = Array.isArray(marketData.indexExpiryDateList)
      ? marketData.indexExpiryDateList
      : [];
    setIndexExpiryDateList(expiryDates);
    setSelectedExpiryDate((currentExpiryDate) => {
      if (expiryDates.includes(currentExpiryDate)) {
        return currentExpiryDate;
      }

      return expiryDates.includes(marketData.expiryDate)
        ? marketData.expiryDate
        : expiryDates[0] || "";
    });
    // console.log("Received market data update:", marketData);
    const sortedRows = Array.isArray(marketData.indexOptionDataModelList)
      ? sortByStrike(marketData.indexOptionDataModelList)
      : [];

    const marketSpotPrice = pickFirstFiniteNumber(
      marketData?.indexLiveDataModel?.lp,
      sortedRows[0]?.spotPrice,
      marketData?.indexDayRange?.niftySpotPrice
    );

    setIndexSymbolName(
      formatSpotSymbolName(
        sortedRows[0]?.indexSymbol || indexFilterValueChanges.split(":")[1] || "NIFTY"
      )
    );
    setAllOptionRows(sortedRows);
    setVisibleOptionRows(filterOptionRows(sortedRows, marketSpotPrice, selectedStrikeFilter));
  }, [marketData, indexFilterValueChanges, selectedStrikeFilter]);

  useEffect(() => {
    const isCurrentExpiry = !selectedExpiryDate || selectedExpiryDate === currentExpiryDate;

    if (activeView !== "ltpAnalysis" || isCurrentExpiry) {
      setPreviousExpiryRows([]);
      setPreviousExpiryError("");
      setIsPreviousExpiryLoading(false);
      return undefined;
    }

    const expirySymbol = buildExpirySymbol(selectedExpiryDate);

    if (!expirySymbol) {
      setPreviousExpiryRows([]);
      setPreviousExpiryError("Invalid expiry date selected.");
      setIsPreviousExpiryLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadPreviousExpiryData = async () => {
      setPreviousExpiryRows([]);
      setPreviousExpiryError("");
      setIsPreviousExpiryLoading(true);

      try {
        const response = await axios.get(INDEX_PREVIOUS_LTP_DATA_URL, {
          params: {
            indexSymbol: indexFilterValueChanges,
            expiryDate: selectedExpiryDate,
            expirySymbol,
          },
          signal: controller.signal,
        });

        setPreviousExpiryRows(normalizePreviousLtpRows(response.data));
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Unable to load previous expiry LTP data", error);
          setPreviousExpiryRows([]);
          setPreviousExpiryError("Unable to load the selected expiry data.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsPreviousExpiryLoading(false);
        }
      }
    };

    loadPreviousExpiryData();

    return () => controller.abort();
  }, [activeView, currentExpiryDate, indexFilterValueChanges, selectedExpiryDate]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setBlinkSignal((previousValue) => !previousValue);
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  usePageTitle(indexLiveDataModel?.lp ?? "N/A", marketData?.expiryDate ?? "N/A");

  const currentSpotPrice = pickFirstFiniteNumber(
    options?.[indexFilterValueChanges]?.indexLiveDataModel?.lp,
    indexLiveDataModel?.lp,
    allOptionRows[0]?.spotPrice
  );

  const previousDayHigh = parseMaybeNumber(indexPivotPointModel?.previousDayHigh);
  const previousDayLow = parseMaybeNumber(indexPivotPointModel?.previousDayLow);
  const previousDayClose = parseMaybeNumber(indexPivotPointModel?.previousDayClose);
  const dayHigh = parseMaybeNumber(indexPivotPointModel?.dayHigh);
  const dayLow = parseMaybeNumber(indexPivotPointModel?.dayLow);
  const isNearPDH =
    previousDayHigh != null &&
    currentSpotPrice != null &&
    Math.abs(previousDayHigh - currentSpotPrice) <= 20;
  const isNearPDL =
    previousDayLow != null &&
    currentSpotPrice != null &&
    Math.abs(previousDayLow - currentSpotPrice) <= 20;
  const isNearPDC =
    previousDayClose != null &&
    currentSpotPrice != null &&
    Math.abs(previousDayClose - currentSpotPrice) <= 20;
  const isNearDH =
    dayHigh != null &&
    currentSpotPrice != null &&
    Math.abs(dayHigh - currentSpotPrice) <= 20;
  const isNearDL =
    dayLow != null &&
    currentSpotPrice != null &&
    Math.abs(dayLow - currentSpotPrice) <= 20;

  const indiaVixValue = pickFirstFiniteNumber(
    options?.["NSE:INDIAVIX"]?.indexLiveDataModel?.lp,
    options?.["NSE:INDIA VIX"]?.indexLiveDataModel?.lp,
    indexLiveDataModel?.indiaVix
  );

  const visibleStats = buildHeaderStats(visibleOptionRows);
  const callResistance = getCallResistanceLogic(
    visibleStats.highestCallVolStrike,
    visibleStats.highestCallOiStrike,
    visibleStats.spotPrice ?? currentSpotPrice
  );
  const putSupport = getPutSupportLogic(
    visibleStats.highestPutVolStrike,
    visibleStats.highestPutOiStrike,
    visibleStats.spotPrice ?? currentSpotPrice
  );
  const marketSignal = getBuySellSignal(visibleStats);

  const renderBuySellSignal = () => {
    if (marketSignal === "SELL_ON_RISE") {
      return (
        <span className={`header-signal sell-rise ${blinkSignal ? "blink" : ""}`}>
          SELL ON RISE
        </span>
      );
    }

    if (marketSignal === "BUY_ON_DEEP") {
      return (
        <span className={`header-signal buy-deep ${blinkSignal ? "blink" : ""}`}>
          BUY ON DEEP
        </span>
      );
    }

    return <span className="header-signal neutral">NO SIGNAL</span>;
  };

  const headerData = [

    {
      value1: renderBuySellSignal(),
    },
    {
      label1: "RES",
      value1: `${formatPlainNumber(callResistance, 0)} - ${getCallResistanceWttAndWtb(
        callResistance,
        visibleStats.secondHighestCallVolStrike,
        visibleStats.secondHighestCallVolume,
        visibleStats.highestCallVolume
      )}`,
      label2: "SUP",
      value2: `${formatPlainNumber(putSupport, 0)} - ${getPutSupportWttAndWtb(
        visibleStats.secondHighestPutVolStrike,
        visibleStats.secondHighestPutVolume,
        putSupport,
        visibleStats.highestPutVolume
      )}`,
    },
    {
      label1: "PDH",
      value1: formatPlainNumber(indexPivotPointModel?.previousDayHigh, 2),
      label2: "PDL",
      value2: formatPlainNumber(indexPivotPointModel?.previousDayLow, 2),
      highlight: isNearPDH,
      highlight2: isNearPDL,
    },
    {
      label1: "PDC",
      value1: formatPlainNumber(indexPivotPointModel?.previousDayClose, 2),
      label2: "OPEN",
      value2: formatPlainNumber(indexPivotPointModel?.indexOpenPrice, 2),
      highlight: isNearPDC,
    },
    {
      label1: "DH",
      value1: formatPlainNumber(indexPivotPointModel?.dayHigh, 2),
      label2: "DL",
      value2: formatPlainNumber(indexPivotPointModel?.dayLow, 2),
      highlight: isNearDH,
      highlight2: isNearDL,
    },
    {
      label1: "ExpDate",
      value1: marketData?.expiryDate || "--",
      label2: "DateTime",
      value2: marketData?.currentDateTime || "--",
    },
  ];
  const leftHeaderData = headerData.filter((item) => item.label1 !== "ExpDate");
  const rightHeaderData = headerData.find((item) => item.label1 === "ExpDate") || null;

  const allMetrics = buildMetrics(allOptionRows);
  const visibleMetrics = buildMetrics(visibleOptionRows);
  const sharedMinuteVolumeSource = resolveFirstNestedListSource(
    marketData,
    allOptionRows,
    ["optionVolume30MDataList"]
  );
  const topCallMinuteVolumeData = buildMinuteVolumePanelData(
    sharedMinuteVolumeSource
    ?? resolveNestedListSource(marketData, allOptionRows, "ce10MVolumeDataList"),
    "call"
  );
  const topPutMinuteVolumeData = buildMinuteVolumePanelData(
    sharedMinuteVolumeSource
    ?? resolveNestedListSource(marketData, allOptionRows, "pe10MVolumeDataList"),
    "put"
  );
  const minuteVolumePanels = [
    {
      key: "call",
      cardClassName: "is-call",
      title: "CALL VOLUME",
      totalLabel: "TOTAL CALL VOLUME SUM",
      barLabel: "CALL VOLUME SUM",
      data: topCallMinuteVolumeData,
    },
    {
      key: "put",
      cardClassName: "is-put",
      title: "PUT VOLUME",
      totalLabel: "TOTAL PUT VOLUME SUM",
      barLabel: "PUT VOLUME SUM",
      data: topPutMinuteVolumeData,
    },
  ];
  const minuteVolumeSignalRows = buildMinuteVolumeSignalRows(
    topCallMinuteVolumeData.rows,
    topPutMinuteVolumeData.rows
  );
  const hasMinuteVolumeRows =
    topCallMinuteVolumeData.rows.length > 0 || topPutMinuteVolumeData.rows.length > 0;
  const volumeSignalCallSum = hasMinuteVolumeRows
    ? topCallMinuteVolumeData.totalVolume
    : visibleMetrics.callVolumeTotal;
  const volumeSignalPutSum = hasMinuteVolumeRows
    ? topPutMinuteVolumeData.totalVolume
    : visibleMetrics.putVolumeTotal;
  const volumeSignal = getVolumeBuySellSignal(volumeSignalCallSum, volumeSignalPutSum);
  const volumeSignalClassName = getVolumeSignalClassName(volumeSignal);
  const strikeStep = getStrikeStep(visibleOptionRows);
  const chartDateLabel = formatChartDate(marketData?.currentDateTime);
  const isOptionOiView = activeView === "optionOi";
  const isOptionOiChangeView = activeView === "optionOiChange";
  const isVolumeView = activeView === "volume";
  const isLtpAnalysisView = activeView === "ltpAnalysis";
  const isGreeksAnalysisView = activeView === "greeksAnalysis";
  const greeksIndexDataList = normalizeGreeksIndexData(indexGreeksAnalyzeData);
  const greeksIndexSymbols = [
    ...new Set(greeksIndexDataList.map((item) => item.indexSymbol).filter(Boolean)),
  ];
  const activeGreeksSymbol = greeksIndexSymbols.includes(selectedGreeksSymbol)
    ? selectedGreeksSymbol
    : greeksIndexSymbols[0] || "";
  const greeksDateOptions = [
    ...new Set(
      greeksIndexDataList
        .filter((item) => item.indexSymbol === activeGreeksSymbol)
        .map((item) => item.createdDate)
        .filter(Boolean)
    ),
  ];
  const activeGreeksDate = selectedGreeksDate || greeksDateOptions[0] || "";
  const liveGreeksIndexData =
    greeksIndexDataList.find(
      (item) =>
        item.indexSymbol === activeGreeksSymbol &&
        (!activeGreeksDate || item.createdDate === activeGreeksDate)
    ) ?? greeksIndexDataList.find((item) => item.indexSymbol === activeGreeksSymbol);
  const isCurrentGreeksSearchResult =
    greeksSearchResult?.createdDate === activeGreeksDate &&
    greeksSearchResult?.createdTime === selectedGreeksTime &&
    greeksSearchResult?.indexSymbol === activeGreeksSymbol;
  const activeGreeksIndexData = isCurrentGreeksSearchResult
    ? greeksSearchResult.data
    : liveGreeksIndexData;
  const allGreeksAnalysisRows = sortByStrike(
    activeGreeksIndexData?.indexOptionDataModelList ?? []
  );
  const greeksSpotPrice = pickFirstFiniteNumber(
    allGreeksAnalysisRows[0]?.spotPrice,
    options?.[activeGreeksSymbol]?.indexLiveDataModel?.lp,
    activeGreeksSymbol === indexFilterValueChanges ? currentSpotPrice : null
  );
  const greeksAnalysisRows = filterOptionRows(
    allGreeksAnalysisRows,
    greeksSpotPrice,
    greeksStrikeFilter
  );
  const greeksTotals = greeksAnalysisRows.reduce(
    (totals, row) => ({
      ceVega: totals.ceVega + toNumber(row.ceVega),
      ceGamma: totals.ceGamma + toNumber(row.ceGamma),
      ceTheta: totals.ceTheta + toNumber(row.ceTheta),
      ceDelta: totals.ceDelta + toNumber(row.ceDelta),
      peVega: totals.peVega + toNumber(row.peVega),
      peGamma: totals.peGamma + toNumber(row.peGamma),
      peTheta: totals.peTheta + toNumber(row.peTheta),
      peDelta: totals.peDelta + toNumber(row.peDelta),
    }),
    {
      ceVega: 0,
      ceGamma: 0,
      ceTheta: 0,
      ceDelta: 0,
      peVega: 0,
      peGamma: 0,
      peTheta: 0,
      peDelta: 0,
    }
  );

  const handleGreeksSubmit = async () => {
    if (!activeGreeksSymbol || !activeGreeksDate || !selectedGreeksTime) {
      setGreeksSearchError("Please select index, date, and time.");
      return;
    }

    setIsGreeksSearchLoading(true);
    setGreeksSearchError("");
    const requestId = greeksSearchRequestRef.current + 1;
    greeksSearchRequestRef.current = requestId;

    try {
      const response = await axios.get(INDEX_OPTION_GREEKS_DATA_URL, {
        params: {
          createdDate: activeGreeksDate,
          createdTime: selectedGreeksTime,
          indexSymbol: activeGreeksSymbol,
        },
      });
      const responsePayload = response.data?.data ?? response.data;
      const [indexOptionData] = normalizeGreeksIndexData(responsePayload);

      if (!indexOptionData) {
        throw new Error("No Greeks analysis data found.");
      }

      if (requestId !== greeksSearchRequestRef.current) {
        return;
      }

      setGreeksSearchResult({
        createdDate: activeGreeksDate,
        createdTime: selectedGreeksTime,
        indexSymbol: activeGreeksSymbol,
        data: indexOptionData,
      });
    } catch (error) {
      if (requestId !== greeksSearchRequestRef.current) {
        return;
      }

      console.error("Unable to load Greeks analysis data", error);
      setGreeksSearchError(
        error.response?.data?.message ||
        error.message ||
        "Unable to load Greeks analysis data."
      );
    } finally {
      if (requestId === greeksSearchRequestRef.current) {
        setIsGreeksSearchLoading(false);
      }
    }
  };

  const handleGreeksReset = () => {
    greeksSearchRequestRef.current += 1;
    setGreeksSearchResult(null);
    setGreeksSearchError("");
    setIsGreeksSearchLoading(false);
    setSelectedGreeksDate("");
    setSelectedGreeksTime("09:15");
  };
  const showOiOverlay = isOptionOiView && showOiChange;
  const cardTitle = isOptionOiChangeView
    ? `OI Change on ${chartDateLabel}`
    : isVolumeView
      ? `Volume on ${chartDateLabel}`
      : isLtpAnalysisView
        ? `LTP Analysis on ${chartDateLabel}`
        : `Open Interest on ${chartDateLabel}`;

  const chartRows = visibleOptionRows
    .map((row) => {
      const strikePrice = parseMaybeNumber(row.strikePrice);

      if (strikePrice == null) {
        return null;
      }

      return {
        strikePrice,
        putOi: clampNonNegative(row.peOI),
        putOiChange: toNumber(row.peOIChg),
        putVolume: clampNonNegative(row.peVolume),
        putVolumePercentageValue: getBarPercentageValue(row.peVolume, visibleStats.highestPutVolume),
        putVolumePercentageLabel: formatBarPercentageLabel(row.peVolume, visibleStats.highestPutVolume),
        putVolumeBadgeVariant: getVolumeBadgeVariant(
          row.peVolume,
          visibleStats.highestPutVolume,
          visibleStats.secondHighestPutVolume
        ),
        callOi: clampNonNegative(row.ceOI),
        callOiChange: toNumber(row.ceOIChg),
        callVolume: clampNonNegative(row.ceVolume),
        callVolumePercentageValue: getBarPercentageValue(
          row.ceVolume,
          visibleStats.highestCallVolume
        ),
        callVolumePercentageLabel: formatBarPercentageLabel(
          row.ceVolume,
          visibleStats.highestCallVolume
        ),
        callVolumeBadgeVariant: getVolumeBadgeVariant(
          row.ceVolume,
          visibleStats.highestCallVolume,
          visibleStats.secondHighestCallVolume
        ),
        ...buildOiSegments(row.peOI, row.peOIChg),
        callSegments: buildOiSegments(row.ceOI, row.ceOIChg),
      };
    })
    .filter(Boolean);

  const isPreviousExpirySelected =
    Boolean(selectedExpiryDate) && selectedExpiryDate !== currentExpiryDate;
  const previousExpiryVisibleRows = filterOptionRows(
    sortByStrike(previousExpiryRows),
    currentSpotPrice,
    selectedStrikeFilter
  );
  const ltpAnalysisSourceRows = isPreviousExpirySelected
    ? previousExpiryVisibleRows
    : visibleOptionRows;
  const ltpAnalysisRows = ltpAnalysisSourceRows
    .map((row) => {
      const strikePrice = parseMaybeNumber(row.strikePrice);

      if (strikePrice == null) {
        return null;
      }

      return {
        strikePrice,
        callMaxLow: parseMaybeNumber(row.ceMaxLow),
        callMaxHigh: parseMaybeNumber(row.ceMaxHigh),
        callLtp: parseMaybeNumber(row.ceLtp),
        callMaxHighLowPercentage: pickFirstFiniteNumber(
          row.ceMaxHighLowPercentage,
          getMaxHighLowPercentage(row.ceMaxLow, row.ceMaxHigh)
        ),
        callMaxHighLtpPercentage: pickFirstFiniteNumber(
          row.ceMaxHighLtpPercentage,
          getMaxHighLtpPercentage(row.ceLtp, row.ceMaxHigh)
        ),
        putLtp: parseMaybeNumber(row.peLtp),
        putMaxHighLowPercentage: pickFirstFiniteNumber(
          row.peMaxHighLowPercentage,
          getMaxHighLowPercentage(row.peMaxLow, row.peMaxHigh)
        ),
        putMaxHighLtpPercentage: pickFirstFiniteNumber(
          row.peMaxHighLtpPercentage,
          getMaxHighLtpPercentage(row.peLtp, row.peMaxHigh)
        ),
        putMaxHigh: parseMaybeNumber(row.peMaxHigh),
        putMaxLow: parseMaybeNumber(row.peMaxLow),
      };
    })
    .filter(Boolean)
    .sort((firstRow, secondRow) => firstRow.strikePrice - secondRow.strikePrice);
  const ltpClosestStrike = ltpAnalysisRows.reduce((closestStrike, row) => {
    if (currentSpotPrice == null) {
      return closestStrike;
    }

    if (closestStrike == null) {
      return row.strikePrice;
    }

    return Math.abs(row.strikePrice - currentSpotPrice) < Math.abs(closestStrike - currentSpotPrice)
      ? row.strikePrice
      : closestStrike;
  }, null);

  const overallPcr = allMetrics.pcrOi;
  const activePcr = isVolumeView ? allMetrics.pcrVolume : overallPcr;
  const visiblePcr = isVolumeView ? visibleMetrics.pcrVolume : visibleMetrics.pcrOi;
  const visiblePutLabel = isVolumeView ? "Total Put Volume" : "Total Put OI";
  const visibleCallLabel = isVolumeView ? "Total Call Volume" : "Total Call OI";
  const visiblePutValue = isVolumeView ? visibleMetrics.putVolumeTotal : visibleMetrics.putOiTotal;
  const visibleCallValue = isVolumeView ? visibleMetrics.callVolumeTotal : visibleMetrics.callOiTotal;
  const visibleVolumeSignal = getVolumeBuySellSignal(
    visibleMetrics.callVolumeTotal,
    visibleMetrics.putVolumeTotal
  );
  const visibleVolumeSignalClassName = getVolumeSignalClassName(visibleVolumeSignal);
  const volumePutColor = CALL_COLOR;
  const volumeCallColor = PUT_COLOR;
  const chartSurfaceWidth = chartRows.length > 14 ? `${chartRows.length * 74}px` : "100%";
  const helperText =
    isOptionOiView
      ? showOiOverlay
        ? "Solid green and red bars show the live put and call OI. Hatched overlays mark OI increase and hollow overlays mark OI decrease for each strike. The dashed line marks the live spot price."
        : "Green bars show put open interest and red bars show call open interest for the visible strike range. The dashed line marks the live spot price."
      : isVolumeView
        ? "Red bars show put volume and green bars show call volume for the visible strike range. The dashed line marks the live spot price."
        : isLtpAnalysisView
          ? "The LTP analysis table shows ATH, ATL, ATH-ATL percentage, ATH-LTP percentage, and LTP for both call and put sides."
          : "Green bars show put OI change and red bars show call OI change. Positive bars rise above zero and negative bars fall below zero for each strike. The dashed line marks the live spot price.";

  const chartData = isOptionOiView
    ? showOiOverlay
      ? {
        datasets: [
          {
            label: "Put OI",
            tooltipLabel: "Put OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.base })),
            backgroundColor: PUT_COLOR,
            borderColor: PUT_COLOR,
            stack: "put",
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Increase",
            tooltipLabel: "Put Increase",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.increase })),
            backgroundColor: (context) => getStripePattern(context.chart, PUT_COLOR, PUT_PATTERN_FILL),
            borderColor: PUT_COLOR,
            stack: "put",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
          {
            label: "Decrease",
            tooltipLabel: "Put Decrease",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.decrease })),
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            borderColor: PUT_COLOR,
            stack: "put",
            borderWidth: 1.3,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
          {
            label: "Call OI",
            tooltipLabel: "Call OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callSegments.base })),
            backgroundColor: CALL_COLOR,
            borderColor: CALL_COLOR,
            stack: "call",
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Increase",
            tooltipLabel: "Call Increase",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callSegments.increase })),
            backgroundColor: (context) =>
              getStripePattern(context.chart, CALL_COLOR, CALL_PATTERN_FILL),
            borderColor: CALL_COLOR,
            stack: "call",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
          {
            label: "Decrease",
            tooltipLabel: "Call Decrease",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callSegments.decrease })),
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            borderColor: CALL_COLOR,
            stack: "call",
            borderWidth: 1.3,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
        ],
      }
      : {
        datasets: [
          {
            label: "Put OI",
            tooltipLabel: "Put OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.putOi })),
            backgroundColor: PUT_COLOR,
            borderColor: PUT_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Call OI",
            tooltipLabel: "Call OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callOi })),
            backgroundColor: CALL_COLOR,
            borderColor: CALL_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
        ],
      }
    : isVolumeView
      ? {
        datasets: [
          {
            label: "Put Volume",
            tooltipLabel: "Put Volume",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.putVolume })),
            valuePercentages: chartRows.map((row) => row.putVolumePercentageValue),
            valueLabels: chartRows.map((row) => row.putVolumePercentageLabel),
            valueVariants: chartRows.map((row) => row.putVolumeBadgeVariant),
            showBarTopLabels: true,
            backgroundColor: volumePutColor,
            borderColor: volumePutColor,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Call Volume",
            tooltipLabel: "Call Volume",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callVolume })),
            valuePercentages: chartRows.map((row) => row.callVolumePercentageValue),
            valueLabels: chartRows.map((row) => row.callVolumePercentageLabel),
            valueVariants: chartRows.map((row) => row.callVolumeBadgeVariant),
            showBarTopLabels: true,
            backgroundColor: volumeCallColor,
            borderColor: volumeCallColor,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
        ],
      }
      : {
        datasets: [
          {
            label: "Put OI chg",
            tooltipLabel: "Put OI change",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.putOiChange })),
            backgroundColor: PUT_COLOR,
            borderColor: PUT_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Call OI chg",
            tooltipLabel: "Call OI change",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callOiChange })),
            backgroundColor: CALL_COLOR,
            borderColor: CALL_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
        ],
      };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        top: 42,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 14,
          boxHeight: 14,
          padding: 22,
          color: "#364152",
          font: {
            size: 13,
            weight: "500",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.94)",
        titleColor: "#ffffff",
        bodyColor: "#e5e7eb",
        borderColor: "rgba(148, 163, 184, 0.25)",
        borderWidth: 1,
        displayColors: true,
        filter: (tooltipItem) =>
          isOptionOiChangeView ? Number(tooltipItem.raw?.y) !== 0 : Number(tooltipItem.raw?.y) > 0,
        callbacks: {
          title: (items) => (items.length ? `Strike ${formatNumber(items[0].parsed.x, 0)}` : ""),
          label: (context) =>
            `${context.dataset.tooltipLabel || context.dataset.label}: ${formatCompactIndianValue(
              context.parsed.y
            )}`,
        },
      },
      spotMarker: {
        enabled: currentSpotPrice != null,
        spotValue: currentSpotPrice,
        label:
          currentSpotPrice != null
            ? `${indexSymbolName} ${formatPrice(currentSpotPrice, 1)}`
            : "",
        lineColor: "rgba(15, 23, 42, 0.65)",
      },
      volumePercentageLabel: {
        enabled: isVolumeView,
        labelOffset: 8,
      },
    },
    scales: {
      x: {
        type: "linear",
        stacked: showOiOverlay,
        grid: {
          color: "rgba(148, 163, 184, 0.14)",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#5b6474",
          stepSize: strikeStep,
          autoSkip: false,
          padding: 6,
          maxRotation: 45,
          minRotation: 45,
          callback: (value) => formatNumber(value, 0),
        },
      },
      y: {
        stacked: showOiOverlay,
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#5b6474",
          callback: (value) => formatAxisValue(value),
        },
        title: {
          display: true,
          text: isOptionOiChangeView
            ? "Call / Put OI Change"
            : isVolumeView
              ? "Call / Put Volume"
              : "Call / Put OI",
          color: "#4b5563",
          font: {
            size: 13,
            weight: "600",
          },
        },
      },
    },
  };

  return (
    <div className="oi-visual-page">
      <div
        className={`oi-visual-shell ${isGreeksAnalysisView ? "is-greeks-active" : ""
          }`}
      >
        <header className="header">
          <nav className="nav-container">
            <div className="header-flex">
              <div className="left-nav">
                <div className="logo-container">
                  <img
                    src="/assets/images/noptionwave-logo-1.png"
                    className="header-logo"
                    alt="noptionwave logo"
                  />
                  <select
                    name="indexFilter"
                    className="index-filter"
                    value={indexFilterValueChanges}
                    onChange={(event) => setIndexFilterValueChangeFilter(event.target.value)}
                    style={{ cursor: "pointer" }}
                    aria-label="Index selector"
                  >
                    {INDEX_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {leftHeaderData.map((item, index) => (
                  <div className="header-item" key={`${item.label1 || "signal"}-${index}`}>
                    {(item.label1 != null || item.value1 != null) && (
                      <span className={`header-line ${item.highlight ? "blink-highlight" : ""}`}>
                        {item.label1 != null && item.label1 !== "" && (
                          <>
                            {item.label1}
                            {item.value1 != null && " : "}
                          </>
                        )}
                        {item.value1 != null && item.value1}
                      </span>
                    )}

                    {(item.label2 != null || item.value2 != null) && (
                      <span className={`header-line ${item.highlight2 ? "blink-highlight" : ""}`}>
                        {item.label2 != null && item.label2 !== "" && (
                          <>
                            {item.label2}
                            {item.value2 != null && " : "}
                          </>
                        )}
                        {item.value2 != null && item.value2}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {rightHeaderData ? (
                <div className="right-nav">
                  <span className="header-line">
                    {rightHeaderData.label1} : {rightHeaderData.value1}
                  </span>
                  <span className="header-line">
                    {rightHeaderData.label2} : {rightHeaderData.value2}
                  </span>
                </div>
              ) : null}
            </div>
          </nav>
        </header>

        <div
          className={`oi-visual-tab-container ${isGreeksAnalysisView ? "is-greeks-active" : ""
            }`}
        >
          <div className="oi-visual-tabs" role="tablist" aria-label="OI analysis views">
            <button
              type="button"
              className={`oi-visual-tab ${isOptionOiView ? "is-active" : ""}`}
              onClick={() => setActiveView("optionOi")}
            >
              OPTION OI
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isOptionOiChangeView ? "is-active" : ""}`}
              onClick={() => setActiveView("optionOiChange")}
            >
              OPTION OICHG
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isVolumeView ? "is-active" : ""}`}
              onClick={() => setActiveView("volume")}
            >
              VOLUME
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isLtpAnalysisView ? "is-active" : ""}`}
              onClick={() => setActiveView("ltpAnalysis")}
            >
              LTP ANALYSIS
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isGreeksAnalysisView ? "is-active" : ""}`}
              onClick={() => setActiveView("greeksAnalysis")}
            >
              GREEKS ANALYSIS
            </button>
          </div>
        </div>

        <section
          className={
            isLtpAnalysisView || isGreeksAnalysisView
              ? "oi-ltp-layout"
              : "oi-visual-card"
          }
        >
          {!isLtpAnalysisView && !isGreeksAnalysisView ? (
            <div className="oi-visual-card-head">
              <div className="oi-visual-card-title-group">
                <h2>{cardTitle}</h2>
                <button
                  type="button"
                  className="oi-visual-link-button"
                  onClick={() => setShowGuide((currentValue) => !currentValue)}
                >
                  {/* How to read this? */}
                </button>
              </div>

              <div className="oi-visual-card-actions">
                <div className="oi-visual-card-stat">
                  <span>PCR</span>
                  <strong>{formatRatio(activePcr)}</strong>
                </div>

                <div className="oi-visual-filter-group">
                  <span>Range</span>
                  <select
                    className="oi-visual-select"
                    value={selectedStrikeFilter}
                    onChange={(event) => setSelectedStrikeFilter(event.target.value)}
                    aria-label="Visible strike range"
                  >
                    {RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isOptionOiView ? (
                  <label className="oi-visual-toggle">
                    <input
                      type="checkbox"
                      checked={showOiChange}
                      onChange={(event) => setShowOiChange(event.target.checked)}
                    />
                    <span className="oi-visual-toggle-slider" />
                    <span className="oi-visual-toggle-label">Show OI</span>
                    <span className="oi-visual-badge">New</span>
                  </label>
                ) : null}
              </div>
            </div>
          ) : null}

          {showGuide && !isGreeksAnalysisView ? (
            <div className="oi-visual-guide">{helperText}</div>
          ) : null}

          {isGreeksAnalysisView ? (
            <>
              <div className="oi-greeks-control-row">
                <strong>GREEKS ANALYSIS</strong>
                <label className="oi-greeks-symbol-control">
                  <span>INDEX</span>
                  <select
                    value={activeGreeksSymbol}
                    onChange={(event) => setSelectedGreeksSymbol(event.target.value)}
                    disabled={greeksIndexSymbols.length === 0}
                    aria-label="Greeks index symbol"
                  >
                    {greeksIndexSymbols.length > 0 ? (
                      greeksIndexSymbols.map((symbol) => (
                        <option key={symbol} value={symbol}>
                          {symbol}
                        </option>
                      ))
                    ) : (
                      <option value="">NO INDEX DATA</option>
                    )}
                  </select>
                </label>
                <label className="oi-greeks-symbol-control">
                  <span>DATE</span>
                  <DatePicker
                    selected={parseGreeksDate(activeGreeksDate)}
                    onChange={(date) => setSelectedGreeksDate(formatGreeksDate(date))}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    filterDate={(date) => !isGreeksWeekend(date)}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    scrollableYearDropdown
                    shouldCloseOnSelect
                    showPopperArrow={false}
                    popperPlacement="bottom"
                    className="oi-greeks-date-picker"
                    calendarClassName="oi-greeks-date-calendar"
                    popperClassName="oi-greeks-date-popper"
                    aria-label="Greeks analysis date"
                  />
                </label>
                <label className="oi-greeks-symbol-control">
                  <span>TIME</span>
                  <select
                    value={selectedGreeksTime}
                    onChange={(event) => setSelectedGreeksTime(event.target.value)}
                    aria-label="Greeks analysis time"
                  >
                    {GREEKS_TIME_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="oi-greeks-find-button"
                  onClick={handleGreeksSubmit}
                  disabled={isGreeksSearchLoading}
                >
                  {isGreeksSearchLoading ? "LOADING..." : "SUBMIT"}
                </button>
                <button
                  type="button"
                  className="oi-greeks-find-button oi-greeks-reset-button"
                  onClick={handleGreeksReset}
                >
                  RESET
                </button>
                <span className="oi-greeks-expiry-label">
                  EXPIRY : {activeGreeksIndexData?.expiryDate || "--"}
                </span>
                {greeksSearchError ? (
                  <span className="oi-greeks-search-error">{greeksSearchError}</span>
                ) : null}
              </div>

              {greeksAnalysisRows.length > 0 ? (
                <div className="oi-greeks-table-wrap">
                  <table className="responsive-table table-bordered text-center sticky-header oi-greeks-table">
                    <thead>
                      <tr>
                        <th className="oi-ltp-call-head">VEGA</th>
                        <th className="oi-ltp-call-head">GAMMA</th>
                        <th className="oi-ltp-call-head">THETA</th>
                        <th className="oi-ltp-call-head">DELTA</th>
                        <th className="oi-ltp-call-head">IV</th>
                        <th className="oi-ltp-call-head">T.V</th>
                        <th className="oi-ltp-call-head">I.V</th>
                        <th className="oi-ltp-call-head">LTP</th>
                        <th className="oi-ltp-call-head">REV</th>
                        <th className="oi-ltp-index-head">
                          <select
                            className="oi-ltp-range-select"
                            value={greeksStrikeFilter}
                            onChange={(event) => setGreeksStrikeFilter(event.target.value)}
                            aria-label="Visible Greeks strike range"
                          >
                            {RANGE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </th>
                        <th className="oi-ltp-put-head">REV</th>
                        <th className="oi-ltp-put-head">LTP</th>
                        <th className="oi-ltp-put-head">I.V</th>
                        <th className="oi-ltp-put-head">T.V</th>
                        <th className="oi-ltp-put-head">IV</th>
                        <th className="oi-ltp-put-head">DELTA</th>
                        <th className="oi-ltp-put-head">THETA</th>
                        <th className="oi-ltp-put-head">GAMMA</th>
                        <th className="oi-ltp-put-head">VEGA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {greeksAnalysisRows.map((row, rowIndex) => {
                        const nextRow = greeksAnalysisRows[rowIndex + 1];
                        const isSpotBetween =
                          nextRow &&
                          greeksSpotPrice != null &&
                          greeksSpotPrice > Math.min(row.strikePrice, nextRow.strikePrice) &&
                          greeksSpotPrice < Math.max(row.strikePrice, nextRow.strikePrice);

                        return (
                          <React.Fragment key={`greeks-analysis-${row.strikePrice ?? rowIndex}`}>
                            <tr>
                              <td>{formatPlainNumber(row.ceVega, 2)}</td>
                              <td>{formatPlainNumber(row.ceGamma, 4)}</td>
                              <td>{formatPlainNumber(row.ceTheta, 2)}</td>
                              <td>{formatPlainNumber(row.ceDelta, 2)}</td>
                              <td>{formatPlainNumber(row.ceIV, 2)}</td>
                              <td>{formatPlainNumber(row.ceTimeValue, 2)}</td>
                              <td>
                                {formatPlainNumber(row.ceIncentricValue ?? row.ceIntrinsicValue, 2)}
                              </td>
                              <td>{formatPlainNumber(row.ceLtp, 2)}</td>
                              <td>{formatPlainNumber(row.ceReversal, 2)}</td>
                              <td className="oi-greeks-strike-cell">
                                {formatPlainNumber(row.strikePrice, 0)}
                              </td>
                              <td>{formatPlainNumber(row.peReversal, 2)}</td>
                              <td>{formatPlainNumber(row.peLtp, 2)}</td>
                              <td>
                                {formatPlainNumber(row.peIncentricValue ?? row.peIntrinsicValue, 2)}
                              </td>
                              <td>{formatPlainNumber(row.peTimeValue, 2)}</td>
                              <td>{formatPlainNumber(row.peIV, 2)}</td>
                              <td>{formatPlainNumber(row.peDelta, 2)}</td>
                              <td>{formatPlainNumber(row.peTheta, 2)}</td>
                              <td>{formatPlainNumber(row.peGamma, 4)}</td>
                              <td>{formatPlainNumber(row.peVega, 2)}</td>
                            </tr>

                            {isSpotBetween ? (
                              <tr className="oi-greeks-spot-row">
                                <td colSpan="2" className="oi-greeks-ce-totals">
                                  VEGA: {formatPlainNumber(greeksTotals.ceVega, 2)}
                                </td>
                                <td colSpan="2" className="oi-greeks-ce-totals">
                                  GAMMA:{" "}{formatPlainNumber(greeksTotals.ceGamma, 4)}
                                </td>
                                <td colSpan="2" className="oi-greeks-ce-totals">
                                  THETA:{" "}{formatPlainNumber(greeksTotals.ceTheta, 2)}
                                </td>
                                <td colSpan="2" className="oi-greeks-ce-totals">
                                  DELTA:{" "}
                                  {formatPlainNumber(greeksTotals.ceDelta, 2)}
                                </td>
                                <td colSpan="3" className="oi-greeks-spot-value">
                                  SPOT - {formatPlainNumber(greeksSpotPrice, 2)}
                                </td>
                                <td colSpan="2" className="oi-greeks-pe-totals">
                                  VEGA: {formatPlainNumber(greeksTotals.peVega, 2)}
                                </td>
                                <td colSpan="2" className="oi-greeks-pe-totals">
                                  GAMMA:{" "} {formatPlainNumber(greeksTotals.peGamma, 4)}
                                </td>
                                <td colSpan="2" className="oi-greeks-pe-totals">
                                  THETA:{" "} {formatPlainNumber(greeksTotals.peTheta, 2)}
                                </td>
                                <td colSpan="2" className="oi-greeks-pe-totals">
                                  DELTA:{" "} {formatPlainNumber(greeksTotals.peDelta, 2)}
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="oi-visual-empty-state">
                  Waiting for live Greeks analysis data...
                </div>
              )}
            </>
          ) : isLtpAnalysisView ? (
            ltpAnalysisRows.length > 0 ? (
              <div className="oi-ltp-table-wrap">
                <table className="responsive-table table-bordered text-center sticky-header oi-ltp-table">
                  <thead>
                    <tr>

                      <th className="oi-ltp-call-head">ATH</th>
                      <th className="oi-ltp-call-head">ATL</th>
                      <th className="oi-ltp-call-head">ATH-ATL(%)</th>
                      <th className="oi-ltp-call-head">ATH-LTP(%)</th>
                      <th className="oi-ltp-call-head">LTP</th>
                      <th className="oi-ltp-index-head">
                        <div className="oi-ltp-header-filters">
                          <select
                            className="oi-ltp-range-select"
                            value={selectedStrikeFilter}
                            onChange={(event) => setSelectedStrikeFilter(event.target.value)}
                            aria-label="Visible strike range"
                          >
                            {RANGE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <select
                            className="oi-ltp-expiry-select"
                            value={selectedExpiryDate}
                            onChange={(event) => setSelectedExpiryDate(event.target.value)}
                            disabled={indexExpiryDateList.length === 0}
                            aria-label="Index expiry date selector"
                          >
                            {indexExpiryDateList.length === 0 ? (
                              <option value="">EXPIRY</option>
                            ) : (
                              indexExpiryDateList.map((expiryDate) => (
                                <option key={expiryDate} value={expiryDate}>
                                  {expiryDate}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                      </th>
                      <th className="oi-ltp-put-head">LTP</th>
                      <th className="oi-ltp-put-head">ATH-LTP(%)</th>
                      <th className="oi-ltp-put-head">ATH-ATL(%)</th>
                      <th className="oi-ltp-put-head">ATL</th>
                      <th className="oi-ltp-put-head">ATH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ltpAnalysisRows.map((row, rowIndex) => {
                      const nextRow = ltpAnalysisRows[rowIndex + 1];
                      const isCallNearMaxLow = isPointDifferenceWithinLimit(
                        row.callMaxLow,
                        row.callLtp
                      );
                      const isPutNearMaxLow = isPointDifferenceWithinLimit(
                        row.putMaxLow,
                        row.putLtp
                      );
                      const callHighLowTierClass = getLtpRangeTierClass(
                        row.callMaxHighLowPercentage
                      );
                      const callHighLtpTierClass = getLtpRangeTierClass(
                        row.callMaxHighLtpPercentage
                      );
                      const putHighLtpTierClass = getLtpRangeTierClass(
                        row.putMaxHighLtpPercentage
                      );
                      const putHighLowTierClass = getLtpRangeTierClass(
                        row.putMaxHighLowPercentage
                      );
                      const isSpotBetween =
                        nextRow &&
                        currentSpotPrice != null &&
                        currentSpotPrice > Math.min(row.strikePrice, nextRow.strikePrice) &&
                        currentSpotPrice < Math.max(row.strikePrice, nextRow.strikePrice);

                      return (
                        <React.Fragment key={`ltp-analysis-${row.strikePrice}`}>
                          {/* <tr className={row.strikePrice === ltpClosestStrike ? "is-active-strike" : ""}> */}
                          <tr>

                            <td>{formatPrice(row.callMaxHigh, 2)}</td>
                            <td className={isCallNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.callMaxLow, 2)}
                            </td>
                            <td className={callHighLowTierClass}>
                              {formatPercentValue(row.callMaxHighLowPercentage, 2)}
                            </td>
                            <td className={callHighLtpTierClass}>
                              {formatPercentValue(row.callMaxHighLtpPercentage, 2)}
                            </td>
                            <td className={isCallNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.callLtp, 2)}
                            </td>
                            <td className="oi-ltp-strike-cell">{formatNumber(row.strikePrice, 0)}</td>
                            <td className={isPutNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.putLtp, 2)}
                            </td>
                            <td className={putHighLtpTierClass}>
                              {formatPercentValue(row.putMaxHighLtpPercentage, 2)}
                            </td>
                            <td className={putHighLowTierClass}>
                              {formatPercentValue(row.putMaxHighLowPercentage, 2)}
                            </td>

                            <td className={isPutNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.putMaxLow, 2)}
                            </td>
                            <td>{formatPrice(row.putMaxHigh, 2)}</td>
                          </tr>

                          {isSpotBetween ? (
                            <tr className="oi-ltp-spot-row">
                              <td colSpan="11">
                                SPOT - {formatPrice(currentSpotPrice, 2)}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="oi-visual-empty-state">
                {isPreviousExpirySelected
                  ? isPreviousExpiryLoading
                    ? "Loading selected expiry data..."
                    : previousExpiryError || "No data found for the selected expiry."
                  : "Waiting for live option chain data..."}
              </div>
            )
          ) : chartRows.length > 0 ? (
            <>
              <div className="oi-visual-chart-panel">
                <div className="oi-visual-chart-surface" style={{ width: chartSurfaceWidth }}>
                  <Bar
                    key={`${indexFilterValueChanges}-${selectedStrikeFilter}-${activeView}-${showOiOverlay}`}
                    data={chartData}
                    options={chartOptions}
                    plugins={[spotMarkerPlugin, volumePercentageLabelPlugin]}
                  />
                </div>
              </div>

              {isOptionOiView || isVolumeView ? (
                <div className={`oi-visual-summary ${isVolumeView ? "is-volume-summary" : ""}`}>
                  <div className={`oi-visual-summary-item ${isVolumeView ? "is-volume-detail" : ""}`}>
                    <span className="oi-visual-summary-label">{visibleCallLabel}</span>
                    {isVolumeView ? (
                      <div className="oi-visual-summary-value is-volume-detail">
                        <span>({formatNumber(visibleCallValue, 0)})</span>
                        <strong>{formatCompactIndianValue(visibleCallValue)}</strong>
                      </div>
                    ) : (
                      <strong>{formatCompactIndianValue(visibleCallValue)}</strong>
                    )}
                  </div>

                  {isVolumeView ? (
                    <div
                      className={`oi-visual-summary-item is-volume-signal-summary ${visibleVolumeSignalClassName}`}
                    >
                      <strong>{visibleVolumeSignal}</strong>
                    </div>
                  ) : null}

                  <div className={`oi-visual-summary-item ${isVolumeView ? "is-volume-detail" : ""}`}>
                    <span className="oi-visual-summary-label">{visiblePutLabel}</span>
                    {isVolumeView ? (
                      <div className="oi-visual-summary-value is-volume-detail">
                        <span>({formatNumber(visiblePutValue, 0)})</span>
                        <strong>{formatCompactIndianValue(visiblePutValue)}</strong>
                      </div>
                    ) : (
                      <strong>{formatCompactIndianValue(visiblePutValue)}</strong>
                    )}
                  </div>

                  <div className="oi-visual-summary-item is-pcr">
                    <span className="oi-visual-summary-label">PCR</span>
                    <strong>{formatRatio(visiblePcr)}</strong>
                  </div>
                </div>
              ) : null}

              {isVolumeView ? (
                <section className="oi-minute-volume-section">
                  <div className="oi-minute-volume-grid">
                    {minuteVolumePanels.map((panel) => (
                      <React.Fragment key={panel.key}>
                        <article className={`oi-minute-volume-card ${panel.cardClassName}`}>
                          <div className="oi-minute-volume-card-head">{panel.title}</div>

                          <div className="oi-minute-volume-table-wrap">
                            <table className="oi-minute-volume-table">
                              <thead>
                                <tr>
                                  <th>TIME</th>
                                  <th>{panel.barLabel}</th>
                                </tr>
                              </thead>

                              <tbody>
                                {panel.data.rows.length > 0 ? (
                                  panel.data.rows.map((row) => {
                                    const barValue = row.barVolume;
                                    const barWidth = panel.data.maxVolume > 0
                                      ? `${(toNumber(barValue) / panel.data.maxVolume) * 100}%`
                                      : "0%";

                                    return (
                                      <tr key={`${panel.key}-${row.rank}-${row.minuteLabel}`}>
                                        <td className="oi-minute-volume-minute-cell">{row.minuteLabel}</td>
                                        <td className="oi-minute-volume-bar-cell">
                                          <div className="oi-minute-volume-bar-row">
                                            <div className="oi-minute-volume-bar-track">
                                              <div
                                                className="oi-minute-volume-bar-fill"
                                                style={{ width: barWidth }}
                                              />
                                            </div>
                                            <span className="oi-minute-volume-bar-text">
                                              {formatNumber(barValue, 0)}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td className="oi-minute-volume-empty" colSpan="2">
                                      Waiting for {panel.title.toLowerCase()} data...
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="oi-minute-volume-card-footer">
                            <span>{panel.totalLabel}</span>
                            <strong>{formatNumber(panel.data.totalVolume, 0)}</strong>
                          </div>
                        </article>

                        {panel.key === "call" ? (
                          <article className={`oi-volume-signal-card ${volumeSignalClassName}`}>
                            <div className="oi-volume-signal-card-head">SIGNAL</div>

                            <div className="oi-volume-signal-table-wrap">
                              <table className="oi-volume-signal-table">
                                <thead>
                                  <tr>
                                    <th>SIGNAL</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {minuteVolumeSignalRows.length > 0 ? (
                                    minuteVolumeSignalRows.map((row) => (
                                      <tr key={`volume-signal-${row.rank}-${row.minuteLabel}`}>
                                        <td className="oi-volume-signal-value-cell">
                                          <span className={`oi-volume-signal-badge ${row.signalClassName}`}>
                                            {row.signal}
                                          </span>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td className="oi-volume-signal-empty">
                                        Waiting for signal data...
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>

                            <div className="oi-volume-signal-card-footer">
                              <strong className={`oi-volume-signal-total ${volumeSignalClassName}`}>
                                {volumeSignal}
                              </strong>
                            </div>
                          </article>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : (
            <div className="oi-visual-empty-state">
              Waiting for live option chain data...
            </div>
          )}
        </section>

        <div className="fixed-footer">
          <span>noptionwave © 2026</span>
        </div>
      </div>
    </div>
  );
}
